import { ColDef, GridReadyEvent, IRowNode, RowSelectedEvent } from '@ag-grid-community/core';
import { Box, Flex, Input, useBreakpointValue } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { isAfter, isBefore } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientDto, api } from '../../lib/api';
import { useTranslation } from 'react-i18next';
import { InputGroup } from '@/components/ui/input-group';
import { LinkButton } from '@/components/ui/link-button';
import { Plus, Search } from 'lucide-react';
import { Grid } from '@/components/ui/grid';
import { AccessGuard } from '@/components/access-guard';
import { StatusFilter } from './status-filter';
import { capitalize } from '@/utils/capitalize';

const defaultColumnDef: ColDef<PatientDto> = {
    flex: 1,
    resizable: true,
    floatingFilter: false,
};

const onGridReady = (params: GridReadyEvent) => {
    params.api.setFilterModel({
        status: {
            value: 'intake pending admitted',
        },
    });
};
function formatName(rawName: string) {
    const parts = rawName.split(',').map((part) => capitalize(part.trim().split(/\s+/).join(' ')));

    return parts.join(', ');
}

export function PatientsPage() {
    const { t } = useTranslation();
    const [filter, setFilter] = useState('');
    const navigate = useNavigate();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const query = useQuery({
        queryKey: ['patient', 'list'],
        queryFn: async () => {
            const { data } = await api.patient.get();
            return data;
        },
    });

    const colDefs = useMemo<ColDef<PatientDto>[]>(() => {
        if (isMobile) {
            return [
                {
                    field: 'name',
                    headerName: 'Name',
                    filter: 'agTextColumnFilter',
                    pinned: 'left',
                    lockPinned: true,
                    suppressAutoSize: true,
                    width: 150,
                    minWidth: 150,
                    valueGetter: (params) => {
                        const rawName = params.data?.name || '';

                        return formatName(rawName);
                    },
                },
                {
                    id: 'status',
                    field: 'status',
                    headerName: 'Status',
                    filter: 'agTextColumnFilter',
                    valueGetter: (params) => {
                        return params.data ? t(`PATIENT.STATUS.${params.data.status}`) : '';
                    },
                },
                {
                    id: 'dob',
                    field: 'dob',
                    headerName: 'Date of Birth',
                    filter: 'agDateColumnFilter',
                    filterParams: {
                        comparator: (filterDate: Date, cellValue: string) => {
                            if (isBefore(cellValue, filterDate)) {
                                return -1;
                            } else if (isAfter(cellValue, filterDate)) {
                                return 1;
                            }
                            return 0;
                        },
                    },
                },
                { id: 'branchName', field: 'branchName', headerName: 'Branch', filter: 'agTextColumnFilter' },
            ];
        }
        return [
            { field: 'branchName', headerName: 'Branch', filter: 'agTextColumnFilter' },
            {
                field: 'name',
                headerName: 'Name',
                filter: 'agTextColumnFilter',
                valueGetter: (params) => {
                    const rawName = params.data?.name || '';

                    return formatName(rawName);
                },
            },
            {
                field: 'primaryPhone',
                headerName: 'Contact',
                filter: 'agTextColumnFilter',
                maxWidth: 150,
                valueGetter: (params) => params.data?.primaryPhone || '-',
            },
            {
                field: 'dob',
                headerName: 'Date of Birth',
                filter: 'agDateColumnFilter',
                filterParams: {
                    comparator: (filterDate: Date, cellValue: string) => {
                        if (isBefore(cellValue, filterDate)) {
                            return -1;
                        } else if (isAfter(cellValue, filterDate)) {
                            return 1;
                        }
                        return 0;
                    },
                },
            },
            {
                field: 'status',
                headerName: 'Status',
                filter: StatusFilter,
                valueGetter: (params) => {
                    return params.data ? t(`PATIENT.STATUS.${params.data.status}`) : '';
                },
            },
            { field: 'episodeNumber', headerName: 'Episode', filter: 'agNumberColumnFilter', maxWidth: 100 },
            { field: 'admissionNumber', headerName: 'Admit #', filter: 'agNumberColumnFilter', maxWidth: 110 },
            {
                field: 'primaryInsuranceName',
                headerName: 'Insurance',
                filter: 'agTextColumnFilter',
                valueGetter: (params) => params.data?.primaryInsuranceName || '-',
            },
            {
                field: 'primaryPhysician',
                headerName: 'Provider',
                filter: 'agTextColumnFilter',
                valueGetter: (params) => params.data?.primaryPhysician || '-',
            },
        ];
    }, [isMobile]);

    const handleSelect = (event: RowSelectedEvent<PatientDto>) => {
        if (event.node.isSelected()) {
            if (event.data?.status === 'INTAKE') {
                navigate(`/branch/${event.data.branchId}/admission/${event.data.admissionId}/intake`);
            } else {
                navigate(`/branch/${event.data?.branchId}/admission/${event.data?.admissionId}/patient`);
            }
        }
    };

    const doesExternalFilterPass = useCallback(
        (node: IRowNode<PatientDto>): boolean => {
            if (node.data) {
                return node.data.name.toLowerCase().includes(filter.toLowerCase());
            }
            return true;
        },
        [filter],
    );

    const isExternalFilterPresent = useCallback((): boolean => {
        return !!filter;
    }, [filter]);

    return (
        <Flex px={{ base: '16px', md: 0 }} pt="16px" h="100%" flexDirection="column">
            <Flex alignItems="center" justifyContent="space-between" pb="12px" px={{ base: 0, md: '16px' }} gap="8px">
                <InputGroup w="296px" startElement={<Search />}>
                    <Input
                        type="search"
                        placeholder="Find"
                        h="32px"
                        value={filter}
                        onInput={(event) => {
                            setFilter((event.target as HTMLInputElement).value);
                        }}
                    />
                </InputGroup>
                <AccessGuard permission="intake:create">
                    <Flex>
                        <LinkButton size="xs" to="/intake" textDecoration="none">
                            <Plus /> New Patient
                        </LinkButton>
                    </Flex>
                </AccessGuard>
            </Flex>
            <Box position="relative" w="100%" h="100%" pb="24px">
                <Grid
                    onGridReady={onGridReady}
                    key={isMobile ? 'mobile-grid' : 'grid'}
                    rowSelection="single"
                    columnDefs={colDefs}
                    rowData={query.data}
                    suppressCellFocus
                    defaultColDef={defaultColumnDef}
                    autoSizeStrategy={
                        isMobile
                            ? {
                                  type: 'fitCellContents',
                                  colIds: ['status', 'dob', 'branchName'],
                                  skipHeader: true,
                              }
                            : { type: 'fitGridWidth' }
                    }
                    onRowSelected={handleSelect}
                    doesExternalFilterPass={doesExternalFilterPass}
                    isExternalFilterPresent={isExternalFilterPresent}
                />
            </Box>
        </Flex>
    );
}
